import React, { useContext, useMemo } from 'react';
import ProgressBar from 'common/components/ProgressBar';
import { ContextType as DashboardContextType, DashboardContext } from 'contexts/DashboardContext';
import { countBy } from 'lodash';
import moment from 'moment';
import { Alert, Precheck, Session } from 'globals/interfaces';
import {
  ALERT_TYPE_IDS,
  PRECHECK_STATUS,
  PRECHECK_STEPS,
  SESSION_STATUS,
} from '../../../../common/components/constant';

type Props = {
  invited: number;
};

const getLatestSessionJoinedTimestamp = (alerts?: Record<string, Alert>) => {
  if (!alerts) return null;
  let latestTimestamp: string | null = null;
  Object.values(alerts).forEach((alert: Alert) => {
    if (alert.alert_type_id !== ALERT_TYPE_IDS.SESSION_JOINED) return;
    if (!latestTimestamp || new Date(alert.timestamp).getTime() > new Date(latestTimestamp).getTime()) {
      latestTimestamp = alert.timestamp;
    }
  });
  return latestTimestamp;
};

const getLatestApprovedPrecheckTimestamp = (preChecks?: Record<string, Precheck>) => {
  if (!preChecks) return null;
  let latestTimestamp: string | null = null;
  Object.values(preChecks).forEach((precheck: Precheck) => {
    if (
      precheck.step !== PRECHECK_STEPS.TEST_ENV ||
      precheck.status !== PRECHECK_STATUS.APPROVED
    ) {
      return;
    }
    if (
      !latestTimestamp ||
      new Date(precheck.requested_timestamp).getTime() >
        new Date(latestTimestamp).getTime()
    ) {
      latestTimestamp = precheck.requested_timestamp;
    }
  });
  return latestTimestamp;
};

const AttendeeStatus: React.FC<Props> = (props) => {
  const { currentSlot, sessions } = useContext<DashboardContextType>(DashboardContext);
  const sessionSnapshot = useMemo(
    () => (sessions || []).filter((session: Session) => `${session?.slot_id}` === `${currentSlot?.id}`),
    [sessions, currentSlot?.id],
  );
  const attendeeStatus = useMemo(() => countBy(sessionSnapshot, 'session_status'), [sessionSnapshot]);

  const preFlightPassed = useMemo(() => {
    if (!sessionSnapshot?.length) return 0;
    return sessionSnapshot.reduce((count, session) => {
      if (session?.session_status !== SESSION_STATUS.ONLINE) return count;
      const latestSessionJoinedAlertTimestamp = getLatestSessionJoinedTimestamp(session?.alerts);
      const latestPrecheckTimestamp = getLatestApprovedPrecheckTimestamp(session?.pre_check);
      if (!latestSessionJoinedAlertTimestamp || !latestPrecheckTimestamp) {
        return count;
      }
      if (moment(latestPrecheckTimestamp).isAfter(moment(latestSessionJoinedAlertTimestamp))) {
        return count + 1;
      }
      return count;
    }, 0);
  }, [sessionSnapshot]);

  const startedTest = useMemo(() => {
    return (
      (attendeeStatus.offline || 0) +
      (attendeeStatus.online || 0) +
      (attendeeStatus.created || 0)
    );
  }, [attendeeStatus]);

  const completed = attendeeStatus.completed || 0;
  const terminated = attendeeStatus.terminated || 0;
  const noShow = attendeeStatus.no_show || 0;

  const notJoined = useMemo(() => {
    return Math.max(props.invited - startedTest - completed - terminated - noShow, 0);
  }, [props.invited, startedTest, completed, terminated, noShow]);

  const getPercentage = (value: number) => {
    if (!props.invited) return 0;
    return (value / props.invited) * 100;
  };

  const progressBarData = useMemo(
    () => [
      { label: 'Not Joined', color: 'red', value: notJoined || 0 },
      { label: 'Pre - Test', color: 'blue', value: startedTest - preFlightPassed || 0 },
      { label: 'Started Test', color: 'blue', value: preFlightPassed || 0 },
      { label: 'Finished Test', color: 'blue', value: attendeeStatus.completed || 0 },
      { label: 'No Show', color: 'red', value: attendeeStatus.no_show || 0 },
      { label: 'Terminated', color: 'red', value: attendeeStatus.terminated || 0 },
    ],
    [attendeeStatus, notJoined, preFlightPassed, startedTest],
  );
  return (
    <div className="flex flex-col space-y-6 p-2">
      {progressBarData.map(({ label, color, value }) => (
        <ProgressBar
          key={label}
          label={label}
          color={color}
          value={value}
          percentage={getPercentage(value)}
        />
      ))}
    </div>
  );
};
export default AttendeeStatus;
